<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import SgpAPI from 'dashboard/api/sgp';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { useStore } from 'vuex';

// Props
const props = defineProps({
  identified: {
    type: Object,
    default: null,
  },
  selectedContractId: {
    type: String,
    default: null,
  },
  currentChat: {
    type: Object,
    default: () => ({}),
  },
});

// Emits
const emit = defineEmits([
  'update:selectedContractId',
  'closeCustomer',
  'openPromiseDialog',
]);

// i18n
const { t: $t } = useI18n();

// Customer resources (loaded after identification)
const contractsLoading = ref(false);
const contracts = ref([]);
const titlesLoading = ref(false);
const titles = ref([]);
const titlesPage = ref(1);
const titlesPerPage = ref(5);
const occurrencesLoading = ref(false);
const occurrences = ref([]);
const serviceOrdersLoading = ref(false);
const serviceOrdersData = ref([]);
const expandedOrderId = ref(null);

function toggleOrderDetail(order) {
  const id = order?.id || order?.ocorrencia || order?.numero;
  expandedOrderId.value = expandedOrderId.value === id ? null : id;
}

function orderIdentity(order) {
  return order?.id || order?.ocorrencia || order?.numero;
}
const connectivityLoading = ref(false);
const connectivityStatus = ref(null);

const customerName = computed(() => {
  if (!props.identified) return '—';
  return (
    props.identified.nome ||
    props.identified.name ||
    props.identified.razao_social ||
    props.identified.clienteNome ||
    '—'
  );
});

const customerDocument = computed(() => {
  if (!props.identified) return '—';
  // Remove formatting from document
  const doc =
    props.identified.cpfcnpj ||
    props.identified.documento ||
    props.identified.cpf ||
    props.identified.cnpj ||
    props.identified.clienteCpfcnpj ||
    '';
  return doc
    ? doc
        .replace(/[^\d]/g, '')
        .replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
    : '—';
});

const customerSince = computed(() => {
  if (!props.identified) return null;
  return props.identified.dataCadastro || null;
});

const customerContacts = computed(() => {
  if (!props.identified)
    return { telefones: [], celulares: [], emails: [], outros: [] };
  return (
    props.identified.contatos || {
      telefones: [],
      celulares: [],
      emails: [],
      outros: [],
    }
  );
});

const allContracts = computed(() => {
  // Prioritize fetched contracts, but fallback to customer's embedded contracts
  if (contracts.value && contracts.value.length > 0) {
    return contracts.value;
  }
  if (props.identified && Array.isArray(props.identified.contratos)) {
    return props.identified.contratos;
  }
  return [];
});

const allTitles = computed(() => {
  // Prioritize embedded titles from customer data when available and complete
  // Only use fetched titles if they have more data than embedded ones
  let titlesList = [];

  const embeddedTitles = props.identified?.titulos || [];
  const fetchedTitles = titles.value || [];

  // Use embedded titles if they exist and are more complete than fetched ones
  if (
    embeddedTitles.length > 0 &&
    (fetchedTitles.length === 0 ||
      embeddedTitles.length >= fetchedTitles.length)
  ) {
    titlesList = embeddedTitles;
  } else if (fetchedTitles.length > 0) {
    titlesList = fetchedTitles;
  }

  // Se há um contrato selecionado e múltiplos contratos, filtrar títulos por contrato
  if (props.selectedContractId && allContracts.value.length > 1) {
    return titlesList.filter(title => {
      const contratoId =
        title.clientecontrato_id || title.contrato_id || title.contrato;
      return String(contratoId) === String(props.selectedContractId);
    });
  }

  return titlesList;
});

// Helper function to check if title is overdue
function isTitleOverdue(title) {
  const status = (title.status || title.situacao || '').toLowerCase();
  const isOpen = status === 'aberto' || status === 'open';

  if (!isOpen) return false;

  // Only check by due date - ignore diasAtraso as it can be inconsistent
  const dueDate =
    title.dataVencimento || title.data_vencimento || title.vencimento;
  if (dueDate) {
    const dueDateObj = new Date(dueDate);
    const today = new Date();
    // Remove time component for date comparison
    dueDateObj.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    return dueDateObj < today;
  }

  return false;
}

const customerMetrics = computed(() => {
  const allTits = allTitles.value;

  // Títulos em aberto: apenas status "aberto" (independente se venceu ou não)
  const openTitles = allTits.filter(t => {
    const status = (t.status || t.situacao || '').toLowerCase();
    return status === 'aberto' || status === 'open';
  });

  // Títulos em atraso: status aberto E data vencimento < hoje
  const overdueTitles = openTitles.filter(t => {
    return isTitleOverdue(t);
  });

  const totalDebt = openTitles.reduce((sum, t) => {
    const value = parseFloat(
      t.valorCorrigido || t.valor_corrigido || t.valor || 0
    );
    return sum + value;
  }, 0);

  // Find next due date
  const validDueDates = openTitles
    .map(t => t.dataVencimento || t.data_vencimento || t.vencimento)
    .filter(Boolean)
    .map(d => new Date(d))
    .filter(d => !Number.isNaN(d))
    .sort((a, b) => a - b);

  return {
    totalOpenTitles: openTitles.length,
    overdueCount: overdueTitles.length,
    totalDebt,
    nextDueDate: validDueDates.length ? validDueDates[0] : null,
  };
});

function getContractId(contract) {
  if (!contract) return null;
  const id =
    contract.id ??
    contract.contrato ??
    contract.contrato_id ??
    contract.codigo ??
    contract.numero ??
    contract.clienteContrato ??
    contract.contratoId;
  return id !== undefined && id !== null ? String(id) : null;
}

const contractOptions = computed(() =>
  allContracts.value
    .map(contract => {
      const value = getContractId(contract);
      if (!value) return null;

      const status = contract?.status || contract?.situacao || '';
      const labelPlan =
        (Array.isArray(contract?.servicos) &&
          contract.servicos[0]?.plano?.descricao) ||
        contract?.plano_descricao ||
        '';

      // Montar label com informações essenciais
      const labelParts = [`#${value}`];

      if (status) labelParts.push(status);
      if (labelPlan) labelParts.push(labelPlan);

      const label = labelParts.join(' · ');

      return { value, label, contract };
    })
    .filter(Boolean)
);

const serviceOrders = computed(() => {
  return Array.isArray(serviceOrdersData.value) ? serviceOrdersData.value : [];
});

const SERVICE_ORDERS_DISPLAY_LIMIT = 5;

function parseDateInput(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'number') return new Date(value);
  const str = String(value).trim();
  // Accept ISO or DD/MM/YYYY
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
    const dt = new Date(str);
    return Number.isNaN(dt.getTime()) ? null : dt;
  }
  const match = str.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (match) {
    const [, d, m, y] = match;
    const dt = new Date(Number(y), Number(m) - 1, Number(d));
    return Number.isNaN(dt.getTime()) ? null : dt;
  }
  return null;
}

const normalizedTitles = computed(() => {
  const titlesList = allTitles.value;
  if (!Array.isArray(titlesList)) return [];
  return titlesList
    .map(item => ({
      ...item,
      dueDate: parseDateInput(
        item?.dataVencimento || item?.vencimento || item?.data_vencimento
      ),
      statusLabel: (item?.status || item?.situacao || '')
        .toString()
        .toLowerCase(),
    }))
    .sort((a, b) => {
      if (!a.dueDate && !b.dueDate) return 0;
      if (!a.dueDate) return 1;
      if (!b.dueDate) return -1;
      return a.dueDate.getTime() - b.dueDate.getTime();
    });
});

function titleGroup(title) {
  if (isTitleOverdue(title)) return 'overdue';
  const s = title.statusLabel || '';
  if (s.includes('aberto')) return 'open';
  if (s.includes('pago')) return 'paid';
  if (s.includes('cancelado')) return 'cancelled';
  return 'other';
}

const orderedTitles = computed(() => {
  const all = normalizedTitles.value;
  const buckets = {
    overdue: [],
    open: [],
    paid: [],
    cancelled: [],
    other: [],
  };

  all.forEach(t => {
    buckets[titleGroup(t)].push(t);
  });

  // Overdue: nearest due first (ascending) — operator acts on soonest deadline
  // Open future: ascending by date — upcoming bills in chronological order
  // Paid/cancelled/other: most recent first (descending)
  const byDateAsc = (a, b) => (a.dueDate || 0) - (b.dueDate || 0);
  const byDateDesc = (a, b) => (b.dueDate || 0) - (a.dueDate || 0);

  buckets.overdue.sort(byDateAsc);
  buckets.open.sort(byDateAsc);
  buckets.paid.sort(byDateDesc);
  buckets.cancelled.sort(byDateDesc);
  buckets.other.sort(byDateDesc);

  return [
    ...buckets.overdue,
    ...buckets.open,
    ...buckets.paid,
    ...buckets.cancelled,
    ...buckets.other,
  ];
});

const titleGroupCounts = computed(() => {
  const counts = {
    overdueCount: 0,
    openCount: 0,
    paidCount: 0,
    cancelledCount: 0,
  };
  orderedTitles.value.forEach(t => {
    const g = titleGroup(t);
    if (g === 'overdue') counts.overdueCount += 1;
    else if (g === 'open') counts.openCount += 1;
    else if (g === 'paid') counts.paidCount += 1;
    else if (g === 'cancelled') counts.cancelledCount += 1;
  });
  return counts;
});

const paginatedTitles = computed(() => {
  const start = (titlesPage.value - 1) * titlesPerPage.value;
  const end = start + titlesPerPage.value;
  return orderedTitles.value.slice(start, end);
});

function isGroupBoundary(idx) {
  if (idx === 0) return false;
  const items = paginatedTitles.value;
  return titleGroup(items[idx]) !== titleGroup(items[idx - 1]);
}

const titlesTotalPages = computed(() =>
  Math.ceil(orderedTitles.value.length / titlesPerPage.value)
);

const visibleServiceOrders = computed(() =>
  serviceOrders.value.slice(0, SERVICE_ORDERS_DISPLAY_LIMIT)
);

const serviceOrdersStats = computed(() => {
  const total = serviceOrders.value.length;
  const displayed = visibleServiceOrders.value.length;
  const remaining = Math.max(total - displayed, 0);

  return {
    total,
    displayed,
    remaining,
  };
});

// Dados técnicos completos do contrato selecionado
const technicalDetails = computed(() => {
  if (!props.selectedContractId) return null;

  // Buscar nos dados embedded primeiro
  const embeddedContracts = props.identified?.contratos || [];
  const embeddedContract = embeddedContracts.find(
    c => getContractId(c) === props.selectedContractId
  );

  let contractData = embeddedContract;

  // Se não encontrou nos embedded, buscar nos contratos carregados
  if (!contractData) {
    contractData = contracts.value.find(
      c => getContractId(c) === props.selectedContractId
    );
  }

  if (!contractData) return null;

  const internetService = contractData.servicos?.find(
    s => s.tipo === 'internet'
  );
  if (!internetService) return null;

  const onu = internetService.onu;
  const endereco = internetService.endereco || contractData.endereco;

  return {
    // Dados ONU
    onu: onu
      ? {
          serial: onu.serial,
          rx: onu.rx,
          tx: onu.tx,
          vlan: onu.vlan,
          olt: onu.olt_nome,
          slot: onu.slot,
          pon: onu.pon,
          splitter: onu.splitter?.nome,
          porta: onu.splitter?.porta,
        }
      : null,

    // Configurações de rede
    network: {
      login: internetService.login,
      senha: internetService.senha,
      mac: internetService.mac,
      ip: internetService.ip,
      grupo: internetService.grupo,
    },

    // Configurações WiFi
    wifi: {
      ssid_2g: internetService.wifi_ssid,
      password_2g: internetService.wifi_password,
      channel_2g: internetService.wifi_channel,
      ssid_5g: internetService.wifi_ssid_5,
      password_5g: internetService.wifi_password_5,
      channel_5g: internetService.wifi_channel_5,
    },

    // Localização
    location: endereco
      ? {
          latitude: endereco.latitude,
          longitude: endereco.longitude,
          endereco: endereco,
        }
      : null,

    // Informações do plano
    plano: internetService.plano,

    // Status
    status: {
      servico: internetService.status,
      contrato: contractData.status,
      motivo: contractData.motivo_status,
    },
  };
});

// Paginação dos cards

// Função para clipboard
function copyToClipboard(text) {
  if (navigator.clipboard && window.isSecureContext) {
    navigator.clipboard.writeText(text).then(() => {
      // Poderemos adicionar toast notification aqui no futuro
    });
  } else {
    // Fallback para contextos não-seguros
    const textArea = document.createElement('textarea');
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand('copy');
    } catch (err) {
      // Error copying to clipboard
    }
    document.body.removeChild(textArea);
  }
}

const customerPhones = computed(() => {
  const contacts = customerContacts.value;
  const values = new Set();

  // Add fixed phones and mobile phones from contacts structure
  if (Array.isArray(contacts.telefones)) {
    contacts.telefones.forEach(phone => phone && values.add(phone));
  }
  if (Array.isArray(contacts.celulares)) {
    contacts.celulares.forEach(phone => phone && values.add(phone));
  }
  if (Array.isArray(contacts.outros)) {
    contacts.outros.forEach(phone => phone && values.add(phone));
  }

  // Fallback to direct fields
  const directFields = [
    'telefone',
    'telefone1',
    'telefone_1',
    'telefone2',
    'telefone_2',
    'celular',
    'celular1',
    'celular_1',
    'celular2',
  ];
  directFields.forEach(field => {
    if (props.identified?.[field]) values.add(props.identified[field]);
  });

  return Array.from(values).filter(Boolean);
});

const customerEmails = computed(() => {
  const contacts = customerContacts.value;
  const values = new Set();

  // Add emails from contacts structure
  if (Array.isArray(contacts.emails)) {
    contacts.emails.forEach(email => email && values.add(email));
  }

  // Fallback to direct fields
  ['email', 'email1', 'email2'].forEach(field => {
    if (props.identified?.[field]) values.add(props.identified[field]);
  });

  return Array.from(values).filter(Boolean);
});

const customerAddress = computed(() => {
  const base = props.identified?.endereco || props.identified || {};
  return {
    street: base.logradouro || base.rua || '',
    number: base.numero || base.num || '',
    district: base.bairro || '',
    city: base.cidade || base.municipio || '',
    state: base.estado || base.uf || '',
    zip: base.cep || base.cep_cobranca || '',
    reference: base.referencia || base.ponto_referencia || '',
    complement: base.complemento || '',
  };
});

const contractAddress = computed(() => {
  if (!props.selectedContractId) return null;

  const contract = allContracts.value.find(
    c => getContractId(c) === props.selectedContractId
  );

  if (!contract?.endereco) return null;

  const addr = contract.endereco;
  const contractAddr = {
    street: addr.logradouro || '',
    number: addr.numero || '',
    district: addr.bairro || '',
    city: addr.cidade || '',
    state: addr.uf || '',
    zip: addr.cep || '',
    complement: addr.complemento || '',
  };

  // Verificar se é diferente do endereço principal
  const customerAddr = customerAddress.value;
  const isDifferent =
    contractAddr.street !== customerAddr.street ||
    contractAddr.number !== customerAddr.number ||
    contractAddr.city !== customerAddr.city;

  return isDifferent ? contractAddr : null;
});

const customerType = computed(() => props.identified?.tipo || '');

const contractDetails = computed(() => {
  if (!props.selectedContractId) return null;

  const contract = allContracts.value.find(
    c => getContractId(c) === props.selectedContractId
  );

  if (!contract) return null;

  return {
    vencimento: contract.vencimento,
    status: contract.status,
    dataCadastro: contract.dataCadastro,
    motivo_status: contract.motivo_status,
  };
});

async function fetchContracts() {
  // If customer already has contracts data, use it instead of making another call
  if (props.identified?.contratos && props.identified.contratos.length > 0) {
    contracts.value = props.identified.contratos;
    contractsLoading.value = false;
    // Using contracts from customer data
    return;
  }

  contracts.value = [];
  if (!props.identified) return;

  // Only fetch if we don't have contract data
  try {
    contractsLoading.value = true;
    const payload = {};
    if (props.identified?.id) {
      payload.cliente_id = props.identified.id;
    } else {
      contractsLoading.value = false;
      return;
    }
    // Fetching additional contracts with payload (whitelisted params only)
    const { data } = await SgpAPI.listContracts(payload);
    // Contracts response received
    if (Array.isArray(data)) {
      contracts.value = data;
    } else if (Array.isArray(data?.contratos)) {
      contracts.value = data.contratos;
    } else {
      contracts.value = [];
    }
  } catch (e) {
    // Error fetching contracts handled silently
    // Use embedded contracts as fallback
    contracts.value = props.identified?.contratos || [];
  } finally {
    contractsLoading.value = false;
  }
}

async function fetchTitles() {
  if (!props.identified) return;

  const embeddedTitles = props.identified?.titulos || [];

  // Skip API call if we already have comprehensive embedded data
  if (embeddedTitles.length > 0) {
    titles.value = [];
    return;
  }

  try {
    titlesLoading.value = true;
    const payload = {};
    if (props.selectedContractId) {
      payload.contrato = props.selectedContractId;
    } else if (props.identified?.id) {
      payload.cliente_id = props.identified.id;
    } else {
      titlesLoading.value = false;
      return;
    }

    // Fetching titles with payload including contract
    const { data } = await SgpAPI.listOpenTitles(payload);
    // Titles response received
    // According to the API docs, response has { paginacao: {...}, titulos: [...] }
    if (Array.isArray(data?.titulos)) {
      titles.value = data.titulos;
    } else if (Array.isArray(data)) {
      titles.value = data;
    } else {
      titles.value = [];
    }
  } catch (e) {
    // Error fetching titles - keep titles.value empty to use embedded fallback
    titles.value = [];
  } finally {
    titlesLoading.value = false;
  }
}

async function fetchOccurrences() {
  occurrences.value = [];
  if (!props.selectedContractId) return;
  try {
    occurrencesLoading.value = true;
    const { data } = await SgpAPI.listActiveServiceOrders({
      contrato: props.selectedContractId,
      limit: 5,
    });
    occurrences.value = Array.isArray(data?.ocorrencias)
      ? data.ocorrencias
      : [];
  } catch (e) {
    occurrences.value = [];
  } finally {
    occurrencesLoading.value = false;
  }
}

async function fetchConnectivityStatus() {
  connectivityStatus.value = null;
  if (!props.selectedContractId) return;

  try {
    connectivityLoading.value = true;
    const { data } = await SgpAPI.verifyAccess({
      contrato: props.selectedContractId.toString(),
    });

    connectivityStatus.value = {
      status: data?.status || 0,
      message: data?.msg || $t('SGP.RESULTS.STATUS_UNAVAILABLE'),
      login: data?.login || '',
      lastCheck: new Date().toISOString(),
    };
  } catch (e) {
    connectivityStatus.value = {
      status: 0,
      message: $t('SGP.RESULTS.CONNECTIVITY_ERROR'),
      login: '',
      lastCheck: new Date().toISOString(),
    };
  } finally {
    connectivityLoading.value = false;
  }
}

async function fetchServiceOrders() {
  serviceOrdersData.value = [];
  if (!props.selectedContractId) return;

  try {
    serviceOrdersLoading.value = true;
    const { data } = await SgpAPI.listServiceOrders({
      contrato: props.selectedContractId,
      limit: 10,
    });
    serviceOrdersData.value = Array.isArray(data?.ordens_servicos)
      ? data.ordens_servicos
      : [];
  } catch (e) {
    serviceOrdersData.value = [];
  } finally {
    serviceOrdersLoading.value = false;
  }
}

function formatDate(v) {
  if (!v) return '';
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v);
    return d.toLocaleString();
  } catch (_) {
    return String(v);
  }
}

function formatDateOnly(v) {
  if (!v) return '';
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v);
    return d.toLocaleDateString();
  } catch (_) {
    return String(v);
  }
}

function updateSelectedContractId(value) {
  emit('update:selectedContractId', value);
}

function openPromiseDialog() {
  emit('openPromiseDialog');
}

const store = useStore();
const applyingToContact = ref(false);
const contactId = computed(() => props.currentChat?.meta?.sender?.id || null);
const conversationDisplayId = computed(() => props.currentChat?.id || null);

const getContactById = useMapGetter('contacts/getContactById');
const contactFromStore = computed(() =>
  contactId.value ? getContactById.value(contactId.value) : null
);

// Mirror backend normalization (apply_to_contact.rb) so we can decide whether
// there is any divergence worth applying. Keeps the button hidden when the
// contact is already in sync with SGP, avoiding no-op activity messages.
const SGP_TITLECASE_LOWERCASE = new Set(['de', 'da', 'do', 'das', 'dos', 'e']);
function sgpTitlecase(raw) {
  const text = String(raw || '')
    .trim()
    .replace(/\s+/g, ' ');
  if (!text) return null;
  return text
    .split(' ')
    .map((word, idx) => {
      const down = word.toLowerCase();
      if (idx > 0 && SGP_TITLECASE_LOWERCASE.has(down)) return down;
      return down.charAt(0).toUpperCase() + down.slice(1);
    })
    .join(' ');
}
function sgpEmail(raw) {
  const text = String(raw || '')
    .trim()
    .toLowerCase();
  return text || null;
}
function sgpUf(raw) {
  const text = String(raw || '').trim();
  return text ? text.toUpperCase() : null;
}
function sgpDigits(raw) {
  const text = String(raw || '').replace(/\D+/g, '');
  return text || null;
}
function pickEmail(id) {
  const candidates = [
    ...(Array.isArray(id.emails) ? id.emails : []),
    ...(Array.isArray(id.contatos?.emails) ? id.contatos.emails : []),
    id.email,
    id.email1,
    id.email2,
  ];
  return candidates.find(v => String(v || '').trim()) || null;
}

const pendingSgpUpdates = computed(() => {
  const contact = contactFromStore.value;
  const id = props.identified;
  if (!contact || !id) return [];

  const addAttrs = contact.additional_attributes || {};
  const customAttrs = contact.custom_attributes || {};
  const pending = [];

  const sgpName = sgpTitlecase(id.nome);
  if (sgpName && sgpName !== contact.name) pending.push('name');

  const sgpEmailValue = sgpEmail(pickEmail(id));
  if (sgpEmailValue && sgpEmailValue !== contact.email) pending.push('email');

  const sgpCity = sgpTitlecase(id.endereco?.cidade || id.endereco_cidade);
  if (sgpCity && sgpCity !== addAttrs.city) pending.push('city');

  const sgpState = sgpUf(id.endereco?.uf || id.endereco_uf);
  if (sgpState && sgpState !== addAttrs.state) pending.push('state');

  const sgpCompany = String(id.razao_social || id.razaoSocial || '').trim();
  if (sgpCompany && sgpCompany !== addAttrs.company_name)
    pending.push('company_name');

  const sgpDescription = id.observacao ? String(id.observacao) : null;
  if (sgpDescription && sgpDescription !== addAttrs.description)
    pending.push('description');

  const sgpCpf = sgpDigits(id.cpfcnpj || id.cpfCnpj || id.cpf_cnpj);
  if (sgpCpf && sgpCpf !== customAttrs.cpf_cnpj) pending.push('cpf_cnpj');

  return pending;
});

const canApplyToContact = computed(
  () =>
    !!contactId.value &&
    !!props.identified &&
    pendingSgpUpdates.value.length > 0
);

function selectedContractForLinkage() {
  const list = allContracts.value || [];
  return (
    list.find(c => getContractId(c) === props.selectedContractId) ||
    list[0] ||
    {}
  );
}

async function applyToContactAction() {
  if (!canApplyToContact.value || applyingToContact.value) return;
  applyingToContact.value = true;
  try {
    const contract = selectedContractForLinkage();
    const source = {
      ...props.identified,
      cliente_id:
        props.identified?.cliente_id ||
        props.identified?.clienteId ||
        contract.clienteId ||
        contract.cliente_id ||
        null,
      contrato_id:
        props.identified?.contrato_id ||
        props.identified?.contratoId ||
        contract.contratoId ||
        contract.contrato_id ||
        contract.id ||
        null,
    };
    const { data } = await SgpAPI.applyToContact({
      contact_id: contactId.value,
      conversation_id: conversationDisplayId.value,
      source,
    });
    const applied = data?.applied || [];
    if (applied.length) {
      // Refresh the contact in the Vuex store so the button's diff-based
      // visibility updates immediately after a successful apply.
      await store.dispatch('contacts/show', { id: contactId.value });
      useAlert(
        $t('SGP.ACTIONS.APPLY_TO_CONTACT_SUCCESS', {
          count: applied.length,
        })
      );
    } else {
      useAlert($t('SGP.ACTIONS.APPLY_TO_CONTACT_NOOP'));
    }
  } catch (error) {
    const message =
      error?.response?.data?.error || $t('SGP.ACTIONS.APPLY_TO_CONTACT_ERROR');
    useAlert(message);
  } finally {
    applyingToContact.value = false;
  }
}

// React when user selects a contract
watch(
  allContracts,
  list => {
    if (!list.length) {
      updateSelectedContractId(null);
      return;
    }
    const exists = list.find(
      contract => getContractId(contract) === props.selectedContractId
    );
    if (exists) {
      return;
    }
    const fallback = getContractId(list[0]);
    updateSelectedContractId(fallback);
  },
  { immediate: true }
);

watch(
  () => props.selectedContractId,
  () => {
    fetchOccurrences();
    fetchConnectivityStatus();
    fetchServiceOrders();
    // Only fetch titles if we don't have embedded data
    const embeddedTitles = props.identified?.titulos || [];
    if (embeddedTitles.length === 0) {
      fetchTitles();
    }
  }
);

// Load customer data when identified changes
watch(
  () => props.identified,
  newIdentified => {
    if (newIdentified) {
      const embeddedTitles = newIdentified?.titulos || [];
      const promises = [
        fetchContracts(),
        fetchOccurrences(),
        fetchConnectivityStatus(),
        fetchServiceOrders(),
      ];

      // Only fetch titles if we don't have embedded data
      if (embeddedTitles.length === 0) {
        promises.push(fetchTitles());
      }

      Promise.allSettled(promises);
    }
  },
  { immediate: true }
);
</script>

<template>
  <div v-if="identified">
    <!-- Customer Header (Dashboard pattern) -->
    <div
      class="px-4 py-3 border-b border-n-weak bg-n-solid-2/30 flex flex-col gap-2 md:flex-row md:items-center md:justify-between"
    >
      <div class="flex flex-col gap-0.5">
        <span class="text-sm font-medium text-n-slate-12">{{
          customerName
        }}</span>
        <span class="text-xs text-n-slate-11">
          {{ customerDocument }}
          <span v-if="customerSince">
            {{ $t('SGP.CUSTOMER_CARD.CUSTOMER_SINCE_PREFIX') }}
            {{ formatDate(customerSince)?.split(',')[0] || customerSince }}
          </span>
        </span>
      </div>
      <div class="flex flex-wrap gap-2 md:justify-end">
        <Button
          variant="outline"
          size="sm"
          color="blue"
          icon="i-lucide-banknote"
          :label="$t('SGP.ACTIONS.PROMISE_RELEASE')"
          :disabled="!customerMetrics.overdueCount"
          @click="openPromiseDialog"
        />
        <Button
          v-if="canApplyToContact"
          variant="outline"
          size="sm"
          color="teal"
          icon="i-lucide-user-check"
          :label="$t('SGP.ACTIONS.APPLY_TO_CONTACT')"
          :disabled="applyingToContact"
          :is-loading="applyingToContact"
          @click="applyToContactAction"
        />
        <Button
          variant="outline"
          size="sm"
          color="slate"
          icon="i-lucide-x"
          :label="$t('SGP.CUSTOMER_CARD.CLOSE')"
          @click="emit('closeCustomer')"
        />
      </div>
    </div>
    <!-- Customer Metrics (Dashboard pattern) -->
    <div
      class="grid grid-cols-2 md:grid-cols-4 gap-3 px-4 py-3 border-b border-n-weak bg-gradient-to-r from-n-surface-1 to-n-solid-2/20"
    >
      <div
        class="bg-n-surface-1 rounded-lg p-2.5 border border-n-weak hover:shadow-sm transition-shadow"
      >
        <p class="text-[11px] uppercase tracking-wide text-n-slate-11 mb-1">
          {{ $t('SGP.CUSTOMER_CARD.ACTIVE_CONTRACTS') }}
        </p>
        <p class="text-xl font-bold text-n-slate-12 text-center">
          {{ allContracts?.length ?? $t('SGP.SYMBOLS.DASH') }}
        </p>
      </div>
      <div
        class="bg-n-surface-1 rounded-lg p-2.5 border border-n-weak hover:shadow-sm transition-shadow"
      >
        <p class="text-[11px] uppercase tracking-wide text-n-slate-11 mb-1">
          {{ $t('SGP.CUSTOMER_CARD.OPEN_TITLES') }}
        </p>
        <p class="text-xl font-bold text-n-slate-12 text-center">
          {{ customerMetrics.totalOpenTitles ?? $t('SGP.SYMBOLS.DASH') }}
        </p>
      </div>
      <div
        class="bg-n-surface-1 rounded-lg p-2.5 border border-n-weak hover:shadow-sm transition-shadow"
      >
        <p class="text-[11px] uppercase tracking-wide text-n-slate-11 mb-1">
          {{ $t('SGP.CUSTOMER_CARD.OPEN_VALUE') }}
        </p>
        <p class="text-xl font-bold text-n-slate-12 text-center">
          {{ $t('SGP.CUSTOMER_CARD.CURRENCY_SYMBOL') }}
          {{
            customerMetrics.totalDebt?.toFixed(2)?.replace('.', ',') || '0,00'
          }}
        </p>
      </div>
      <div
        class="bg-n-surface-1 rounded-lg p-2.5 border border-n-weak hover:shadow-sm transition-shadow"
      >
        <p class="text-[11px] uppercase tracking-wide text-n-slate-11 mb-1">
          {{ $t('SGP.CUSTOMER_CARD.NEXT_DUE') }}
        </p>
        <p class="text-xl font-bold text-n-slate-12 text-center">
          {{
            customerMetrics.nextDueDate
              ? formatDateOnly(customerMetrics.nextDueDate)
              : $t('SGP.SYMBOLS.DASH')
          }}
        </p>
      </div>
    </div>

    <!-- Customer Details Sidebar (Dashboard pattern) -->
    <div
      class="px-4 py-3 grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6 border-t border-n-weak bg-gradient-to-br from-n-solid-2/10 to-n-solid-2/5"
    >
      <!-- Customer Info Card -->
      <div
        class="rounded-lg border border-n-weak bg-n-surface-1 shadow-sm hover:shadow-md transition-shadow flex flex-col min-w-0 max-h-96"
      >
        <div
          class="flex flex-col gap-2 px-4 py-3 border-b border-n-weak bg-n-solid-2/50 md:flex-row md:items-center md:justify-between"
        >
          <div class="flex items-center gap-2 justify-between w-full md:w-auto">
            <h3 class="text-sm font-semibold text-n-slate-12">
              {{ $t('SGP.SECTIONS.CUSTOMER_INFO') }}
            </h3>
          </div>
        </div>
        <div class="px-3 py-2 flex flex-col gap-3 flex-1 min-h-0">
          <div class="overflow-y-auto overflow-x-auto flex-1 min-h-0">
            <div class="space-y-3">
              <!-- Contract Selector -->
              <div class="rounded-md border border-n-weak bg-n-solid-2/20 p-3">
                <label class="text-xs font-medium text-n-slate-11 mb-1 block">
                  {{ $t('SGP.CONTRACT_SELECTOR.LABEL') }}
                </label>
                <select
                  :value="selectedContractId"
                  class="w-full rounded bg-n-surface-1 px-2 py-1.5 text-xs text-n-slate-12 !outline-n-weak hover:!outline-n-slate-6 focus:!outline-n-slate-7 !mb-0 !h-auto !rounded !py-1.5"
                  @change="updateSelectedContractId($event.target.value)"
                >
                  <option
                    v-for="option in contractOptions"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </option>
                </select>
                <div class="text-xs text-n-slate-11 mt-1">
                  <span class="font-medium">{{ allContracts.length }}</span>
                  {{ $t('SGP.CUSTOMER.CONTRACTS') }}
                  <span v-if="contractDetails">
                    {{ $t('SGP.SYMBOLS.BULLET')
                    }}{{ $t('SGP.CUSTOMER.DUE_DATE') }}
                    {{ contractDetails.vencimento }}
                  </span>
                </div>
              </div>

              <!-- Customer Data Card -->
              <div
                v-if="
                  customerType || customerAddress.street || customerAddress.city
                "
                class="rounded-md border border-n-weak bg-n-solid-2/20 p-3"
              >
                <div class="flex items-center gap-2 mb-2">
                  <span
                    class="inline-flex h-1.5 w-1.5 rounded-full bg-n-iris-9"
                  />
                  <span class="text-xs font-semibold text-n-slate-12">{{
                    $t('SGP.CUSTOMER_CARD.CUSTOMER_DATA')
                  }}</span>
                </div>
                <div class="grid grid-cols-1 gap-1 text-xs text-n-slate-11">
                  <div v-if="customerType" class="flex justify-between">
                    <span>{{ $t('SGP.CUSTOMER_CARD.CUSTOMER_TYPE') }}</span>
                    <span class="font-medium text-n-slate-12">{{
                      customerType
                    }}</span>
                  </div>
                  <div
                    v-if="customerAddress.street || customerAddress.city"
                    class="flex flex-col gap-0.5 mt-1"
                  >
                    <span>{{ $t('SGP.FORMS.ADDRESS') }}</span>
                    <span class="font-medium text-n-slate-12">
                      {{
                        [customerAddress.street, customerAddress.number]
                          .filter(Boolean)
                          .join(', ')
                      }}{{
                        customerAddress.district
                          ? ', ' + customerAddress.district
                          : ''
                      }}
                      {{
                        [customerAddress.city, customerAddress.state]
                          .filter(Boolean)
                          .join('/')
                      }}{{
                        customerAddress.zip
                          ? ' - CEP: ' + customerAddress.zip
                          : ''
                      }}{{
                        customerAddress.complement
                          ? ' (' + customerAddress.complement + ')'
                          : ''
                      }}
                    </span>
                  </div>
                  <div
                    v-if="contractAddress"
                    class="flex flex-col gap-0.5 mt-1"
                  >
                    <span>{{
                      $t('SGP.CUSTOMER_CARD.INSTALLATION_ADDRESS')
                    }}</span>
                    <span class="font-medium text-n-slate-12">
                      {{
                        [contractAddress.street, contractAddress.number]
                          .filter(Boolean)
                          .join(', ')
                      }}{{
                        contractAddress.district
                          ? ', ' + contractAddress.district
                          : ''
                      }}
                      {{
                        [contractAddress.city, contractAddress.state]
                          .filter(Boolean)
                          .join('/')
                      }}{{
                        contractAddress.zip
                          ? ' - CEP: ' + contractAddress.zip
                          : ''
                      }}{{
                        contractAddress.complement
                          ? ' (' + contractAddress.complement + ')'
                          : ''
                      }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Contacts Card -->
              <div
                v-if="customerPhones.length || customerEmails.length"
                class="rounded-md border border-n-weak bg-n-solid-2/20 p-3"
              >
                <div class="flex items-center gap-2 mb-2">
                  <span
                    class="inline-flex h-1.5 w-1.5 rounded-full bg-n-teal-9"
                  />
                  <span class="text-xs font-semibold text-n-slate-12">{{
                    $t('SGP.SECTIONS.CONTACT_CHANNELS')
                  }}</span>
                </div>
                <div class="grid grid-cols-1 gap-1 text-xs text-n-slate-11">
                  <div
                    v-if="customerPhones.length"
                    class="flex flex-col gap-0.5"
                  >
                    <span>{{ $t('SGP.CUSTOMER_CARD.PHONES_LABEL') }}</span>
                    <div
                      class="flex flex-wrap gap-x-3 gap-y-1 font-medium text-n-slate-12"
                    >
                      <span
                        v-for="(phone, idx) in customerPhones"
                        :key="'phone-' + idx"
                      >
                        {{ phone }}
                      </span>
                    </div>
                  </div>
                  <div
                    v-if="customerEmails.length"
                    class="flex flex-col gap-0.5 mt-1"
                  >
                    <span>{{ $t('SGP.CUSTOMER_CARD.EMAILS_LABEL') }}</span>
                    <div
                      class="flex flex-wrap gap-x-3 gap-y-1 font-medium text-n-slate-12"
                    >
                      <span
                        v-for="(email, idx) in customerEmails"
                        :key="'email-' + idx"
                      >
                        {{ email }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Technical Info Card -->
      <div
        class="rounded-lg border border-n-weak bg-n-surface-1 shadow-sm hover:shadow-md transition-shadow flex flex-col min-w-0 max-h-96"
      >
        <div
          class="flex flex-col gap-2 px-4 py-3 border-b border-n-weak bg-n-solid-2/50 md:flex-row md:items-center md:justify-between"
        >
          <div class="flex items-center gap-2 justify-between w-full md:w-auto">
            <h3 class="text-sm font-semibold text-n-slate-12">
              {{ $t('SGP.SECTIONS.TECHNICAL_INFO') }}
            </h3>
            <Button
              variant="link"
              size="xs"
              color="blue"
              icon="i-lucide-refresh-cw"
              :label="$t('SGP.ACTIONS.REFRESH')"
              @click="fetchConnectivityStatus"
            />
          </div>
        </div>
        <div class="px-3 py-2 flex flex-col gap-3 flex-1 min-h-0">
          <div class="overflow-y-auto overflow-x-auto flex-1 min-h-0">
            <!-- Connectivity Status -->
            <div v-if="connectivityLoading" class="text-center py-4">
              <LoadingState :message="$t('SGP.LOADING')" />
            </div>
            <div v-else-if="connectivityStatus" class="space-y-3">
              <div class="rounded-md border border-n-weak bg-n-solid-2/20 p-2">
                <div class="flex items-center gap-2">
                  <span
                    class="inline-flex h-2.5 w-2.5 rounded-full bg-n-teal-9"
                  />
                  <span class="text-xs font-medium text-n-teal-11">{{
                    $t('SGP.TECHNICAL.CONNECTION_STATUS')
                  }}</span>
                </div>
              </div>
            </div>

            <!-- Technical Details -->
            <div v-if="technicalDetails" class="space-y-3">
              <!-- Plano e Status Card -->
              <div
                v-if="technicalDetails.plano || technicalDetails.status"
                class="rounded-md border border-n-weak bg-n-solid-2/20 p-3"
              >
                <div class="flex items-center gap-2 mb-2">
                  <span
                    class="inline-flex h-1.5 w-1.5 rounded-full bg-n-iris-9"
                  />
                  <span class="text-xs font-semibold text-n-slate-12">{{
                    $t('SGP.TECHNICAL.PLAN_STATUS.TITLE')
                  }}</span>
                </div>
                <div class="grid grid-cols-1 gap-1 text-xs text-n-slate-11">
                  <div
                    v-if="technicalDetails.plano"
                    class="flex justify-between"
                  >
                    <span>{{ $t('SGP.TECHNICAL.PLAN_STATUS.PLAN') }}</span>
                    <span class="font-medium text-n-slate-12">{{
                      technicalDetails.plano.descricao
                    }}</span>
                  </div>
                  <div
                    v-if="technicalDetails.status.servico"
                    class="flex justify-between"
                  >
                    <span>{{ $t('SGP.TECHNICAL.PLAN_STATUS.STATUS') }}</span>
                    <span class="font-medium text-n-slate-12">{{
                      technicalDetails.status.servico
                    }}</span>
                  </div>
                </div>
              </div>

              <!-- ONU Details Card -->
              <div
                v-if="technicalDetails.onu"
                class="rounded-md border border-n-weak bg-n-solid-2/20 p-3"
              >
                <div class="flex items-center gap-2 mb-2">
                  <span
                    class="inline-flex h-1.5 w-1.5 rounded-full bg-n-amber-9"
                  />
                  <span class="text-xs font-semibold text-n-slate-12">{{
                    $t('SGP.TECHNICAL.ONU_EQUIPMENT.TITLE')
                  }}</span>
                </div>
                <div class="grid grid-cols-1 gap-1 text-xs text-n-slate-11">
                  <div
                    v-if="technicalDetails.onu.serial"
                    class="flex justify-between"
                  >
                    <span>{{ $t('SGP.TECHNICAL.ONU_EQUIPMENT.SERIAL') }}</span>
                    <span class="font-medium text-n-slate-12 font-mono">{{
                      technicalDetails.onu.serial
                    }}</span>
                  </div>
                  <div
                    v-if="technicalDetails.onu.olt"
                    class="flex justify-between"
                  >
                    <span>{{ $t('SGP.TECHNICAL.ONU_EQUIPMENT.OLT') }}</span>
                    <span class="font-medium text-n-slate-12">{{
                      technicalDetails.onu.olt
                    }}</span>
                  </div>
                  <div
                    v-if="technicalDetails.onu.slot && technicalDetails.onu.pon"
                    class="flex justify-between"
                  >
                    <span>{{
                      $t('SGP.TECHNICAL.ONU_EQUIPMENT.POSITION')
                    }}</span>
                    <span class="font-medium text-n-slate-12"
                      >{{ $t('SGP.TECHNICAL.ONU_EQUIPMENT.SLOT') }}
                      {{ technicalDetails.onu.slot
                      }}{{ $t('SGP.TECHNICAL.ONU_EQUIPMENT.PON') }}
                      {{ technicalDetails.onu.pon }}</span
                    >
                  </div>
                  <div
                    v-if="technicalDetails.onu.rx"
                    class="flex justify-between"
                  >
                    <span>{{ $t('SGP.TECHNICAL.ONU_EQUIPMENT.RX') }}</span>
                    <span class="font-medium text-n-slate-12"
                      >{{ technicalDetails.onu.rx }}
                      {{ $t('SGP.TECHNICAL.ONU_EQUIPMENT.DBM') }}</span
                    >
                  </div>
                  <div
                    v-if="technicalDetails.onu.tx"
                    class="flex justify-between"
                  >
                    <span>{{ $t('SGP.TECHNICAL.ONU_EQUIPMENT.TX') }}</span>
                    <span class="font-medium text-n-slate-12"
                      >{{ technicalDetails.onu.tx }}
                      {{ $t('SGP.TECHNICAL.ONU_EQUIPMENT.DBM') }}</span
                    >
                  </div>
                  <div
                    v-if="technicalDetails.onu.vlan"
                    class="flex justify-between"
                  >
                    <span>{{ $t('SGP.TECHNICAL.ONU_EQUIPMENT.VLAN') }}</span>
                    <span class="font-medium text-n-slate-12">{{
                      technicalDetails.onu.vlan
                    }}</span>
                  </div>
                  <div
                    v-if="technicalDetails.onu.splitter"
                    class="flex justify-between"
                  >
                    <span>{{
                      $t('SGP.TECHNICAL.ONU_EQUIPMENT.SPLITTER')
                    }}</span>
                    <span class="font-medium text-n-slate-12">
                      {{ technicalDetails.onu.splitter }}
                      <span v-if="technicalDetails.onu.porta"
                        >{{ $t('SGP.SYMBOLS.OPEN_PAREN')
                        }}{{ $t('SGP.TECHNICAL.ONU_EQUIPMENT.PORT') }}
                        {{ technicalDetails.onu.porta
                        }}{{ $t('SGP.SYMBOLS.CLOSE_PAREN') }}</span
                      >
                    </span>
                  </div>
                </div>
              </div>

              <!-- Network Configuration Card -->
              <div
                v-if="technicalDetails.network"
                class="rounded-md border border-n-weak bg-n-solid-2/20 p-3"
              >
                <div class="flex items-center gap-2 mb-2">
                  <span
                    class="inline-flex h-1.5 w-1.5 rounded-full bg-n-teal-9"
                  />
                  <span class="text-xs font-semibold text-n-slate-12">{{
                    $t('SGP.TECHNICAL.NETWORK_ACCESS.TITLE')
                  }}</span>
                </div>
                <div class="grid grid-cols-1 gap-1 text-xs text-n-slate-11">
                  <div
                    v-if="technicalDetails.network.login"
                    class="flex justify-between"
                  >
                    <span>{{ $t('SGP.TECHNICAL.NETWORK_ACCESS.LOGIN') }}</span>
                    <span class="font-medium text-n-slate-12 font-mono">{{
                      technicalDetails.network.login
                    }}</span>
                  </div>
                  <div
                    v-if="technicalDetails.network.mac"
                    class="flex justify-between"
                  >
                    <span>{{ $t('SGP.TECHNICAL.NETWORK_ACCESS.MAC') }}</span>
                    <span class="font-medium text-n-slate-12 font-mono">{{
                      technicalDetails.network.mac
                    }}</span>
                  </div>
                  <div
                    v-if="technicalDetails.network.ip"
                    class="flex justify-between"
                  >
                    <span>{{ $t('SGP.TECHNICAL.NETWORK_ACCESS.IP') }}</span>
                    <span class="font-medium text-n-slate-12 font-mono">{{
                      technicalDetails.network.ip
                    }}</span>
                  </div>
                  <div
                    v-if="technicalDetails.network.grupo"
                    class="flex justify-between"
                  >
                    <span>{{ $t('SGP.TECHNICAL.NETWORK_ACCESS.GROUP') }}</span>
                    <span class="font-medium text-n-slate-12">{{
                      technicalDetails.network.grupo
                    }}</span>
                  </div>
                </div>
              </div>

              <!-- WiFi Configuration Card -->
              <div
                v-if="
                  technicalDetails.wifi &&
                  (technicalDetails.wifi.ssid_2g ||
                    technicalDetails.wifi.ssid_5g)
                "
                class="rounded-md border border-n-weak bg-n-solid-2/20 p-3"
              >
                <div class="flex items-center gap-2 mb-2">
                  <span
                    class="inline-flex h-1.5 w-1.5 rounded-full bg-n-violet-9"
                  />
                  <span class="text-xs font-semibold text-n-slate-12">{{
                    $t('SGP.TECHNICAL.WIFI.TITLE')
                  }}</span>
                </div>
                <div class="space-y-2">
                  <!-- Rede {{ $t('SGP.TECHNICAL.WIFI.BAND_2_4') }} -->
                  <div
                    v-if="technicalDetails.wifi.ssid_2g"
                    class="rounded border border-n-weak/50 bg-n-surface-1/50 p-2"
                  >
                    <div class="text-xs font-medium text-n-slate-11 mb-1">
                      {{ $t('SGP.TECHNICAL.WIFI.BAND_2_4') }}
                    </div>
                    <div class="grid grid-cols-1 gap-1 text-xs text-n-slate-11">
                      <div class="flex justify-between">
                        <span>{{ $t('SGP.TECHNICAL.WIFI.SSID') }}</span>
                        <span class="font-medium text-n-slate-12">{{
                          technicalDetails.wifi.ssid_2g
                        }}</span>
                      </div>
                      <div
                        v-if="technicalDetails.wifi.password_2g"
                        class="flex justify-between"
                      >
                        <span>{{ $t('SGP.TECHNICAL.WIFI.PASSWORD') }}</span>
                        <span class="font-medium text-n-slate-12 font-mono">{{
                          technicalDetails.wifi.password_2g
                        }}</span>
                      </div>
                      <div
                        v-if="technicalDetails.wifi.channel_2g"
                        class="flex justify-between"
                      >
                        <span>{{ $t('SGP.TECHNICAL.WIFI.CHANNEL') }}</span>
                        <span class="font-medium text-n-slate-12">{{
                          technicalDetails.wifi.channel_2g
                        }}</span>
                      </div>
                    </div>
                  </div>
                  <!-- Rede {{ $t('SGP.TECHNICAL.WIFI.BAND_5') }} -->
                  <div
                    v-if="technicalDetails.wifi.ssid_5g"
                    class="rounded border border-n-weak/50 bg-n-surface-1/50 p-2"
                  >
                    <div class="text-xs font-medium text-n-slate-11 mb-1">
                      {{ $t('SGP.TECHNICAL.WIFI.BAND_5') }}
                    </div>
                    <div class="grid grid-cols-1 gap-1 text-xs text-n-slate-11">
                      <div class="flex justify-between">
                        <span>{{ $t('SGP.TECHNICAL.WIFI.SSID') }}</span>
                        <span class="font-medium text-n-slate-12">{{
                          technicalDetails.wifi.ssid_5g
                        }}</span>
                      </div>
                      <div
                        v-if="technicalDetails.wifi.password_5g"
                        class="flex justify-between"
                      >
                        <span>{{ $t('SGP.TECHNICAL.WIFI.PASSWORD') }}</span>
                        <span class="font-medium text-n-slate-12 font-mono">{{
                          technicalDetails.wifi.password_5g
                        }}</span>
                      </div>
                      <div
                        v-if="technicalDetails.wifi.channel_5g"
                        class="flex justify-between"
                      >
                        <span>{{ $t('SGP.TECHNICAL.WIFI.CHANNEL') }}</span>
                        <span class="font-medium text-n-slate-12">{{
                          technicalDetails.wifi.channel_5g
                        }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Location Info Card -->
              <div
                v-if="
                  technicalDetails.location &&
                  (technicalDetails.location.latitude ||
                    technicalDetails.location.longitude)
                "
                class="rounded-md border border-n-weak bg-n-solid-2/20 p-3"
              >
                <div class="flex items-center gap-2 mb-2">
                  <span
                    class="inline-flex h-1.5 w-1.5 rounded-full bg-n-teal-9"
                  />
                  <span class="text-xs font-semibold text-n-slate-12">{{
                    $t('SGP.TECHNICAL.LOCATION.TITLE')
                  }}</span>
                </div>
                <div class="grid grid-cols-1 gap-1 text-xs text-n-slate-11">
                  <div
                    v-if="technicalDetails.location.latitude"
                    class="flex justify-between"
                  >
                    <span>{{ $t('SGP.TECHNICAL.LOCATION.LATITUDE') }}</span>
                    <span class="font-medium text-n-slate-12 font-mono">{{
                      technicalDetails.location.latitude
                    }}</span>
                  </div>
                  <div
                    v-if="technicalDetails.location.longitude"
                    class="flex justify-between"
                  >
                    <span>{{ $t('SGP.TECHNICAL.LOCATION.LONGITUDE') }}</span>
                    <span class="font-medium text-n-slate-12 font-mono">{{
                      technicalDetails.location.longitude
                    }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Fallback quando não há dados técnicos -->
            <div
              v-else-if="!connectivityLoading && !connectivityStatus"
              class="text-center py-8 text-xs text-n-slate-11"
            >
              <div class="rounded-md border border-n-weak bg-n-solid-2/20 p-4">
                <span class="text-n-slate-11">{{
                  $t('SGP.TECHNICAL.NO_INFO')
                }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      class="px-4 py-3 grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6 border-t border-n-weak bg-gradient-to-br from-n-solid-2/10 to-n-solid-2/5"
    >
      <!-- Financial Section -->
      <div
        class="rounded-lg border border-n-weak bg-n-surface-1 shadow-sm hover:shadow-md transition-shadow flex flex-col min-w-0 max-h-96"
      >
        <div
          class="flex flex-col gap-2 px-4 py-3 border-b border-n-weak bg-n-solid-2/50 md:flex-row md:items-center md:justify-between"
        >
          <div class="flex items-center gap-2 justify-between w-full md:w-auto">
            <h3 class="text-sm font-semibold text-n-slate-12">
              {{ $t('SGP.SECTIONS.FINANCIAL') }}
            </h3>
            <Button
              variant="link"
              size="xs"
              color="blue"
              icon="i-lucide-refresh-cw"
              :label="$t('SGP.ACTIONS.REFRESH')"
              @click="fetchTitles"
            />
          </div>
        </div>
        <div v-if="titlesLoading" class="p-4">
          <LoadingState :message="$t('SGP.LOADING')" />
        </div>
        <div v-else class="px-3 py-2 flex flex-col gap-3 flex-1 min-h-0">
          <div
            v-if="!orderedTitles.length"
            class="text-center py-8 text-xs text-n-slate-11"
          >
            {{ $t('SGP.RESULTS.NO_TITLES') }}
          </div>
          <div v-else class="flex flex-col flex-1 min-h-0 gap-3">
            <div
              v-if="orderedTitles.length"
              class="flex flex-wrap items-center gap-3 px-1 text-[11px]"
            >
              <span class="inline-flex items-center gap-1 text-n-ruby-11">
                <span class="h-1.5 w-1.5 rounded-full bg-n-ruby-9" />
                {{ titleGroupCounts.overdueCount }}
                {{ $t('SGP.CUSTOMER_CARD.OVERDUE').toLowerCase() }}
              </span>
              <span class="inline-flex items-center gap-1 text-n-amber-11">
                <span class="h-1.5 w-1.5 rounded-full bg-n-amber-9" />
                {{ titleGroupCounts.openCount }}
                {{ $t('SGP.CUSTOMER_CARD.OPEN_TITLES').toLowerCase() }}
              </span>
              <span class="inline-flex items-center gap-1 text-n-teal-11">
                <span class="h-1.5 w-1.5 rounded-full bg-n-teal-9" />
                {{ titleGroupCounts.paidCount }}
                {{ $t('SGP.FINANCIAL.PAID_LABEL') }}
              </span>
              <span class="inline-flex items-center gap-1 text-n-slate-11">
                <span class="h-1.5 w-1.5 rounded-full bg-n-slate-8" />
                {{ titleGroupCounts.cancelledCount }}
                {{ $t('SGP.FINANCIAL.CANCELLED_LABEL') }}
              </span>
            </div>
            <div class="overflow-y-auto flex-1 min-h-0 pr-1">
              <div class="space-y-3">
                <template v-for="(title, idx) in paginatedTitles" :key="idx">
                  <div
                    v-if="isGroupBoundary(idx)"
                    class="border-t border-dashed border-n-weak my-1"
                  />
                  <div
                    class="rounded-md border border-n-weak bg-n-surface-1 px-3 py-3 shadow-sm hover:shadow-md transition-shadow"
                    :class="{
                      'border-l-2 border-l-n-ruby-9': isTitleOverdue(title),
                      'border-l-2 border-l-n-teal-9': (
                        title.status ||
                        title.situacao ||
                        ''
                      )
                        .toLowerCase()
                        .includes('pago'),
                      'border-l-2 border-l-n-slate-8': (
                        title.status ||
                        title.situacao ||
                        ''
                      )
                        .toLowerCase()
                        .includes('cancelado'),
                    }"
                  >
                    <div class="flex items-start justify-between gap-4">
                      <div class="flex flex-col gap-1 min-w-0">
                        <span class="text-sm font-semibold text-n-slate-12">
                          {{ $t('SGP.FINANCIAL.TITLE_PREFIX')
                          }}{{ title.id || title.titulo_id || '-' }}
                        </span>
                        <span class="text-xs text-n-slate-11">
                          {{ $t('SGP.FINANCIAL.DUE_DATE') }}
                          {{
                            formatDateOnly(
                              title.dataVencimento ||
                                title.data_vencimento ||
                                title.vencimento
                            ) ||
                            title.vencimento ||
                            title.data_vencimento ||
                            '-'
                          }}
                        </span>
                        <span
                          v-if="title.portador"
                          class="text-xs text-n-slate-11"
                        >
                          {{ title.portador }}
                        </span>
                      </div>
                      <div
                        class="flex flex-col items-end gap-2 text-right shrink-0"
                      >
                        <div class="flex items-center gap-3 whitespace-nowrap">
                          <span class="text-sm font-semibold text-n-slate-12">
                            {{ $t('SGP.FINANCIAL.CURRENCY') }}
                            {{
                              (
                                title.valor ||
                                title.valor_total ||
                                title.valor_atual ||
                                0
                              )
                                .toFixed(2)
                                .replace('.', ',')
                            }}
                          </span>
                          <span
                            class="inline-flex items-center px-2 py-1 rounded-full text-[11px] font-medium"
                            :class="{
                              'bg-n-ruby-3/50 text-n-ruby-11':
                                isTitleOverdue(title),
                              'bg-n-amber-3/50 text-n-amber-11':
                                (title.status || title.situacao || '')
                                  .toLowerCase()
                                  .includes('aberto') && !isTitleOverdue(title),
                              'bg-n-teal-3/50 text-n-teal-11': (
                                title.status ||
                                title.situacao ||
                                ''
                              )
                                .toLowerCase()
                                .includes('pago'),
                              'bg-n-slate-3/50 text-n-slate-11': (
                                title.status ||
                                title.situacao ||
                                ''
                              )
                                .toLowerCase()
                                .includes('cancelado'),
                              'bg-n-solid-2/50 text-n-slate-11': !(
                                title.status || title.situacao
                              ),
                            }"
                          >
                            {{ title.status || title.situacao || '-' }}
                          </span>
                        </div>
                        <div
                          v-if="
                            (title.status || title.situacao || '')
                              .toLowerCase()
                              .includes('aberto')
                          "
                          class="flex items-center gap-2 whitespace-nowrap text-xs"
                        >
                          <Button
                            v-if="title.codigoPix || title.codigo_pix"
                            variant="link"
                            size="xs"
                            color="blue"
                            label="PIX"
                            :title="$t('SGP.FINANCIAL.COPY_PIX')"
                            @click="
                              copyToClipboard(
                                title.codigoPix || title.codigo_pix,
                                'PIX copiado!'
                              )
                            "
                          />
                          <Button
                            v-if="title.linhaDigitavel || title.linha_digitavel"
                            variant="link"
                            size="xs"
                            color="blue"
                            label="Boleto"
                            :title="$t('SGP.FINANCIAL.COPY_BOLETO')"
                            @click="
                              copyToClipboard(
                                title.linhaDigitavel || title.linha_digitavel,
                                'Linha digitável copiada!'
                              )
                            "
                          />
                          <Button
                            v-if="title.link || title.url"
                            variant="link"
                            size="xs"
                            color="blue"
                            label="URL"
                            :title="$t('SGP.FINANCIAL.COPY_LINK')"
                            @click="
                              copyToClipboard(
                                title.link || title.url,
                                'URL copiada!'
                              )
                            "
                          />
                        </div>
                      </div>
                    </div>
                    <div
                      v-if="isTitleOverdue(title) && title.dueDate"
                      class="flex items-center gap-1 text-xs mt-1"
                    >
                      <span
                        class="inline-flex h-2 w-2 rounded-full bg-n-ruby-9/80"
                      />
                      <span class="text-n-ruby-11 font-medium">
                        {{
                          Math.floor(
                            (Date.now() - title.dueDate.getTime()) / 86400000
                          )
                        }}
                        {{ $t('SGP.SERVICE_ORDERS.DAYS_OVERDUE') }}
                      </span>
                    </div>
                  </div>
                </template>
              </div>
            </div>
            <div
              v-if="orderedTitles.length"
              class="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5 text-xs text-n-slate-11 border-t border-n-weak bg-n-solid-2/50"
            >
              <span>
                {{ $t('SGP.SERVICE_ORDERS.SHOWING') }}
                {{
                  titlesPage === titlesTotalPages
                    ? orderedTitles.length
                    : titlesPage * titlesPerPage
                }}
                {{ $t('SGP.SERVICE_ORDERS.OF') }}
                {{ orderedTitles.length }}
                {{ $t('SGP.SERVICE_ORDERS.TITLES') }}
              </span>
              <div class="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="xs"
                  color="slate"
                  label="Anterior"
                  :disabled="titlesPage === 1"
                  @click="titlesPage = Math.max(1, titlesPage - 1)"
                />
                <span
                  >{{ titlesPage }}
                  {{ $t('SGP.SERVICE_ORDERS.OF') }}
                  {{ titlesTotalPages }}</span
                >
                <Button
                  variant="outline"
                  size="xs"
                  color="slate"
                  label="Próximo"
                  :disabled="titlesPage === titlesTotalPages"
                  @click="
                    titlesPage = Math.min(titlesTotalPages, titlesPage + 1)
                  "
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Service Orders Section -->
      <div
        class="rounded-lg border border-n-weak bg-n-surface-1 shadow-sm hover:shadow-md transition-shadow flex flex-col min-w-0 max-h-96"
      >
        <div
          class="flex flex-col gap-2 px-4 py-3 border-b border-n-weak bg-n-solid-2/50 md:flex-row md:items-center md:justify-between"
        >
          <div class="flex items-center gap-2 justify-between w-full md:w-auto">
            <h3 class="text-sm font-semibold text-n-slate-12">
              {{ $t('SGP.SECTIONS.SERVICE_ORDERS') }}
            </h3>
            <Button
              variant="link"
              size="xs"
              color="blue"
              icon="i-lucide-refresh-cw"
              :label="$t('SGP.ACTIONS.REFRESH')"
              @click="fetchServiceOrders"
            />
          </div>
        </div>
        <div v-if="serviceOrdersLoading" class="p-4">
          <LoadingState :message="$t('SGP.LOADING')" />
        </div>
        <div v-else class="px-3 py-2 flex flex-col gap-3 flex-1 min-h-0">
          <div
            v-if="!serviceOrdersStats.total"
            class="text-center py-8 text-xs text-n-slate-11"
          >
            {{ $t('SGP.RESULTS.NO_SERVICE_ORDERS') }}
          </div>
          <div v-else class="flex flex-col flex-1 min-h-0 gap-3">
            <div class="overflow-y-auto flex-1 min-h-0 pr-1">
              <div class="space-y-3">
                <div
                  v-for="(order, idx) in visibleServiceOrders"
                  :key="idx"
                  class="flex flex-col gap-1 rounded-md border border-n-weak bg-n-solid-2/40 px-3 py-2 shadow-sm cursor-pointer hover:border-n-slate-6 transition-colors"
                  @click="toggleOrderDetail(order)"
                >
                  <div class="flex items-center justify-between text-sm">
                    <span class="font-medium text-n-slate-12">
                      {{ $t('SGP.SERVICE_ORDERS.OS_NUMBER')
                      }}{{ order.id || order.numero || order.codigo || '-' }}
                    </span>
                    <span
                      class="inline-flex items-center px-2 py-0.5 rounded-full text-xs"
                      :class="{
                        'bg-n-teal-3/50 text-n-teal-11': (
                          order.status ||
                          order.situacao ||
                          ''
                        )
                          .toLowerCase()
                          .includes('finalizado'),
                        'bg-n-amber-3/50 text-n-amber-11': (
                          order.status ||
                          order.situacao ||
                          ''
                        )
                          .toLowerCase()
                          .includes('agendado'),
                        'bg-n-iris-3/50 text-n-iris-11': (
                          order.status ||
                          order.situacao ||
                          ''
                        )
                          .toLowerCase()
                          .includes('aberto'),
                        'bg-n-solid-2/50 text-n-slate-11': !(
                          order.status || order.situacao
                        ),
                      }"
                    >
                      {{ order.status || order.situacao || '-' }}
                    </span>
                  </div>
                  <div class="text-xs text-n-slate-11 flex flex-col gap-0.5">
                    <span v-if="order.motivo">{{ order.motivo }}</span>
                    <span v-if="order.tipo">{{ order.tipo }}</span>
                    <span v-if="order.data_agendamento">
                      {{ $t('SGP.SERVICE_ORDERS.SCHEDULED') }}
                      {{ formatDateOnly(order.data_agendamento) }}
                    </span>
                    <span v-else-if="order.data_cadastro">
                      {{ $t('SGP.SERVICE_ORDERS.CREATED') }}
                      {{ formatDateOnly(order.data_cadastro) }}
                    </span>
                    <span v-if="order.responsavel || order.tecnico">
                      {{ $t('SGP.SERVICE_ORDERS.TECHNICIAN') }}
                      {{ order.responsavel || order.tecnico }}
                    </span>
                    <span v-if="order.pop" class="text-n-slate-11">
                      {{ $t('SGP.TECHNICAL.ONU_EQUIPMENT.OLT') }}
                      {{ order.pop }}
                    </span>
                  </div>
                  <div
                    v-if="expandedOrderId === orderIdentity(order)"
                    class="mt-2 pt-2 border-t border-dashed border-n-weak text-xs text-n-slate-11 flex flex-col gap-1"
                    @click.stop
                  >
                    <div v-if="order.ocorrencia">
                      <span class="font-medium text-n-slate-12">
                        {{ $t('SGP.SERVICE_ORDERS.OCCURRENCE') }}
                      </span>
                      {{ order.ocorrencia }}
                    </div>
                    <div v-if="order.contrato">
                      <span class="font-medium text-n-slate-12">
                        {{ $t('SGP.CONTRACT_SELECTOR.LABEL') }}
                      </span>
                      #{{ order.contrato }}
                      <span
                        v-if="order.contrato_status"
                        class="text-n-slate-11"
                      >
                        ({{ order.contrato_status }})
                      </span>
                    </div>
                    <div v-if="order.data_cadastro">
                      <span class="font-medium text-n-slate-12">
                        {{ $t('SGP.SERVICE_ORDERS.CREATED') }}
                      </span>
                      {{ formatDateOnly(order.data_cadastro) }}
                    </div>
                    <div v-if="order.data_finalizacao">
                      <span class="font-medium text-n-slate-12">
                        {{ $t('SGP.SERVICE_ORDERS.FINISHED') }}
                      </span>
                      {{ formatDateOnly(order.data_finalizacao) }}
                    </div>
                    <div
                      v-if="
                        order.tecnicos_auxiliares &&
                        order.tecnicos_auxiliares.length
                      "
                    >
                      <span class="font-medium text-n-slate-12">
                        {{ $t('SGP.SERVICE_ORDERS.AUX_TECHS') }}
                      </span>
                      {{ order.tecnicos_auxiliares.join(', ') }}
                    </div>
                    <div v-if="order.usuario">
                      <span class="font-medium text-n-slate-12">
                        {{ $t('SGP.SERVICE_ORDERS.CREATED_BY') }}
                      </span>
                      {{ order.usuario }}
                    </div>
                    <div v-if="order.usuario_finalizacao">
                      <span class="font-medium text-n-slate-12">
                        {{ $t('SGP.SERVICE_ORDERS.CLOSED_BY') }}
                      </span>
                      {{ order.usuario_finalizacao }}
                    </div>
                    <div
                      v-if="
                        order.classificacoes &&
                        (order.classificacoes.classificacao ||
                          order.classificacoes.nome)
                      "
                    >
                      <span class="font-medium text-n-slate-12">
                        {{ $t('SGP.SERVICE_ORDERS.CLASSIFICATION') }}
                      </span>
                      {{
                        order.classificacoes.classificacao ||
                        order.classificacoes.nome
                      }}
                    </div>
                    <div v-if="order.conteudo" class="mt-1">
                      <div class="font-medium text-n-slate-12 mb-0.5">
                        {{ $t('SGP.SERVICE_ORDERS.CONTENT') }}
                      </div>
                      <p
                        class="whitespace-pre-wrap text-n-slate-11 bg-n-solid-2/40 rounded px-2 py-1"
                      >
                        {{ order.conteudo }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div
              class="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5 text-xs text-n-slate-11 border-t border-n-weak bg-n-solid-2/50"
            >
              <span>
                {{ $t('SGP.SERVICE_ORDERS.SHOWING') }}
                {{ serviceOrdersStats.displayed }}
                {{ $t('SGP.SERVICE_ORDERS.OF') }}
                {{ serviceOrdersStats.total }}
                {{ $t('SGP.SERVICE_ORDERS.ORDERS') }}
              </span>
              <span
                v-if="serviceOrdersStats.remaining > 0"
                class="font-medium text-n-slate-12"
              >
                {{ $t('SGP.SYMBOLS.PLUS') }}{{ serviceOrdersStats.remaining }}
                {{ $t('SGP.SERVICE_ORDERS.ADDITIONAL') }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
